import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, IndianRupee, Trophy, Clock, AlertTriangle, Bell, FileSpreadsheet,
  Award, ShieldCheck, Calendar, Target, Sparkles, MessageCircle, Pencil,
  CheckCircle2, ListChecks, History, Settings, Filter as FilterIcon,
} from "lucide-react";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import DiscussButton from "@/components/v2/DiscussButton";

const fmtCr = (n) =>
  n == null || n === 0 ? "—" : `₹${(n / 1e7).toFixed(2)} Cr`;
const fmt = (n) =>
  n == null ? "—" : new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n);

function TodayStrip({ items, navigate }) {
  if (!items.length) return null;
  const cls = (sev) => sev === "urgent"
    ? "bg-rose-50 border-rose-200 text-rose-800"
    : sev === "warn"
    ? "bg-amber-50 border-amber-200 text-amber-800"
    : "bg-blue-50 border-blue-200 text-blue-800";
  return (
    <Card className="border-blue-200 bg-gradient-to-r from-blue-50/30 to-white">
      <CardContent className="py-3 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-wider text-blue-700 font-bold flex items-center gap-1">
            <Activity className="w-3 h-3" /> Do today
          </p>
          <span className="text-[10px] text-slate-400">
            ranked by urgency
          </span>
        </div>
        <div className="space-y-1.5">
          {items.slice(0, 5).map((it, i) => (
            <button
              key={`${it.kind}-${it.entity_id || i}`}
              onClick={() => it.link && navigate(it.link)}
              className={`w-full text-left flex items-start gap-2 px-2 py-1.5 rounded border ${cls(it.severity)} hover:shadow-sm transition`}
            >
              <span className="text-[9px] font-mono font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-white/50 shrink-0 mt-0.5">
                {it.badge || it.kind}
              </span>
              <span className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{it.title}</p>
                {it.body && (
                  <p className="text-[11px] opacity-80 line-clamp-1">{it.body}</p>
                )}
                {it.project_name && (
                  <p className="text-[10px] opacity-60 mt-0.5">#{it.project_name}</p>
                )}
              </span>
            </button>
          ))}
        </div>
        {items.length > 5 && (
          <p className="text-[10px] text-slate-500 italic text-center">
            + {items.length - 5} more — keep moving.
          </p>
        )}
      </CardContent>
    </Card>
  );
}



function StatCard({ icon: Icon, label, value, sublabel, color = "blue" }) {
  const cls = {
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  }[color];
  return (
    <div className={`rounded-md border p-3 ${cls}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold opacity-80">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <p className="text-lg font-semibold mt-0.5">{value}</p>
      {sublabel && <p className="text-[10px] opacity-70">{sublabel}</p>}
    </div>
  );
}

function StatsStrip({ stats }) {
  if (!stats) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[1,2,3,4,5].map(i => (
          <div key={i} className="rounded-md border bg-slate-50 p-3 h-20 animate-pulse" />
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
      <StatCard
        icon={IndianRupee}
        label="Active pipeline"
        value={fmtCr(stats.active_pipeline_value_inr)}
        sublabel={`${stats.active_bids} live bids`}
        color="blue"
      />
      <StatCard
        icon={Target}
        label="Win rate · 30d"
        value={stats.win_rate_pct_30d != null ? `${stats.win_rate_pct_30d}%` : "—"}
        sublabel={`Lifetime ${stats.win_rate_pct_lifetime ?? "—"}%`}
        color={stats.win_rate_pct_30d == null ? "slate" :
               stats.win_rate_pct_30d >= 50 ? "emerald" :
               stats.win_rate_pct_30d >= 30 ? "amber" : "rose"}
      />
      <StatCard
        icon={Trophy}
        label="This quarter"
        value={`${stats.won_this_quarter}W / ${stats.lost_this_quarter}L`}
        sublabel="won vs lost"
        color="emerald"
      />
      <StatCard
        icon={CheckCircle2}
        label="Completed · month"
        value={stats.completed_this_month}
        sublabel="bids closed (any terminal stage)"
        color="slate"
      />
      <StatCard
        icon={AlertTriangle}
        label="Action needed"
        value={`${stats.pending_reminders + stats.overdue_deadlines}`}
        sublabel={`${stats.pending_reminders} reminders · ${stats.overdue_deadlines} overdue`}
        color={(stats.pending_reminders + stats.overdue_deadlines) > 0 ? "rose" : "slate"}
      />
    </div>
  );
}

function RemindersStrip({ reminders, onSnooze, onComplete }) {
  if (!reminders.length) return null;
  return (
    <Card className="border-amber-200">
      <CardContent className="py-3 space-y-2">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-amber-700 font-bold">
          <Bell className="w-3 h-3" /> Reminders due
        </div>
        {reminders.slice(0, 6).map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-2 text-sm border-l-2 border-amber-300 pl-2">
            <div className="min-w-0">
              <p className="text-slate-800 truncate">
                {r.category ? `Refresh rate list — ${r.category}` : "Refresh overall rate list"}
              </p>
              <p className="text-[10px] text-slate-500">
                Day {r.day_of_month} · next {r.next_due_at}
                {r.last_fired_at && <> · last {r.last_fired_at}</>}
              </p>
            </div>
            <div className="flex gap-1 shrink-0">
              <button
                onClick={() => onSnooze(r)}
                className="text-[10px] text-blue-600 hover:underline"
              >
                +7d
              </button>
              <button
                onClick={() => onComplete(r)}
                className="text-[10px] text-emerald-600 hover:underline"
              >
                done
              </button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function AlertChip({ alert }) {
  const cls = {
    info: "bg-blue-100 text-blue-700 border-blue-200",
    warn: "bg-amber-100 text-amber-700 border-amber-200",
    success: "bg-emerald-100 text-emerald-700 border-emerald-200",
  }[alert.severity] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <Badge className={`${cls} text-[10px]`}>{alert.label}</Badge>
  );
}

function QuickLinks({ product, navigate }) {
  // Quick-action chips per product card. Each one deep-links to the
  // relevant filter / page so any team member can drill in immediately.
  const links = [
    { icon: FileSpreadsheet, label: "BoQ", target: () => navigate(`/v2/projects?product=${encodeURIComponent(product.product_code)}`) },
    { icon: History, label: "History", target: () => navigate(`/v2/historical?category=${encodeURIComponent(product.category || "")}`) },
    { icon: Award, label: "Certs", target: () => navigate(`/v2/feed?filter=certs&product=${encodeURIComponent(product.product_code)}`) },
    { icon: ShieldCheck, label: "EMD/PQC", target: () => navigate(`/v2/feed?filter=emd&product=${encodeURIComponent(product.product_code)}`) },
    { icon: Calendar, label: "Deadlines", target: () => navigate(`/v2/feed?filter=deadlines&product=${encodeURIComponent(product.product_code)}`) },
    { icon: Pencil, label: "Edit", target: () => navigate(`/v2/catalog?product=${encodeURIComponent(product.product_code)}`) },
  ];
  return (
    <div className="flex flex-wrap gap-1 pt-2 border-t">
      {links.map((l) => {
        const Icon = l.icon;
        return (
          <button
            key={l.label}
            onClick={l.target}
            className="text-[10px] px-2 py-0.5 rounded-full border border-slate-200 bg-white hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 flex items-center gap-1"
          >
            <Icon className="w-3 h-3" />
            {l.label}
          </button>
        );
      })}
    </div>
  );
}

function ProductCard({ p, navigate }) {
  const completed = p.active_bids === 0 && (p.won_count + p.lost_count) > 0;
  return (
    <Card className={completed ? "border-emerald-200" : ""}>
      <CardContent className="py-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-slate-900 truncate">{p.product_name}</p>
              <span className="text-[10px] font-mono text-slate-400">{p.product_code}</span>
              {p.category && <Badge variant="outline" className="text-[10px]">{p.category}</Badge>}
              {completed && (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">
                  <CheckCircle2 className="w-3 h-3 inline mr-0.5" /> All bids closed
                </Badge>
              )}
            </div>
            <p className="text-[10px] text-slate-400">
              {p.sku_count} SKU{p.sku_count === 1 ? "" : "s"} ·{" "}
              {p.rfps_mentioned} RFP{p.rfps_mentioned === 1 ? "" : "s"} mentioned this product
            </p>
          </div>
          <div className="shrink-0 flex items-center gap-1">
            <DiscussButton
              entityType="catalog_product"
              entityId={p.product_id}
              title={p.product_name}
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div className="bg-blue-50/50 border border-blue-100 rounded p-1.5">
            <p className="text-[9px] uppercase tracking-wider text-blue-700 font-bold">Active</p>
            <p className="text-sm font-semibold text-blue-900">
              {p.active_bids} <span className="text-[10px] opacity-60 font-normal">bids</span>
            </p>
            <p className="text-[10px] text-blue-700">{fmtCr(p.pipeline_value_inr)}</p>
          </div>
          <div className="bg-emerald-50/50 border border-emerald-100 rounded p-1.5">
            <p className="text-[9px] uppercase tracking-wider text-emerald-700 font-bold">Win rate</p>
            <p className="text-sm font-semibold text-emerald-900">
              {p.win_rate_pct != null ? `${p.win_rate_pct}%` : "—"}
            </p>
            <p className="text-[10px] text-emerald-700">
              {p.won_count}W / {p.lost_count}L
            </p>
          </div>
          <div className="bg-amber-50/50 border border-amber-100 rounded p-1.5">
            <p className="text-[9px] uppercase tracking-wider text-amber-700 font-bold">Win-prob</p>
            <p className="text-sm font-semibold text-amber-900">
              {p.avg_win_probability != null ? `${p.avg_win_probability}%` : "—"}
            </p>
            <p className="text-[10px] text-amber-700">live bids avg</p>
          </div>
        </div>

        {p.alerts.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {p.alerts.map((a, i) => <AlertChip key={i} alert={a} />)}
          </div>
        )}

        {p.hot_clients.length > 0 && (
          <div className="text-[10px] text-slate-500">
            <span className="uppercase tracking-wider font-bold mr-1">Top clients:</span>
            {p.hot_clients.slice(0, 4).map((c) => (
              <span key={c.client_name} className="mr-2">
                {c.client_name} <span className="text-slate-400">({c.project_count})</span>
              </span>
            ))}
          </div>
        )}

        {p.last_excerpts.length > 0 && (
          <div className="space-y-1 border-t pt-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Latest from RFPs
            </p>
            {p.last_excerpts.slice(0, 2).map((ex, i) => (
              <div key={i} className="text-xs">
                <button
                  onClick={() => ex.project_id && navigate(`/v2/projects/${ex.project_id}`)}
                  className="text-slate-700 hover:text-blue-700 text-left line-clamp-2"
                  title={ex.rfp_title}
                >
                  {ex.snippet}
                </button>
                <p className="text-[10px] text-slate-400">
                  {ex.rfp_title}{ex.page ? ` · p${ex.page}` : ""}
                  {ex.matched_on && <> · matched <code className="font-mono">{ex.matched_on}</code></>}
                </p>
              </div>
            ))}
          </div>
        )}

        <QuickLinks product={p} navigate={navigate} />
      </CardContent>
    </Card>
  );
}

export default function FeedPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [todayItems, setTodayItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | active | completed
  const [q, setQ] = useState("");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, p, r, td] = await Promise.all([
        v2Api.feed.stats(),
        v2Api.feed.products(),
        v2Api.reminders.list(),
        v2Api.feed.today(8).catch(() => []),
      ]);
      setStats(s);
      setProducts(p || []);
      setTodayItems(td || []);
      const today = new Date().toISOString().slice(0, 10);
      const due = (r || []).filter((x) => x.is_active && x.next_due_at <= today);
      setReminders(due);
    } catch {
      toast.error("Could not load feed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); }, []);

  const onSnoozeReminder = async (r) => {
    try {
      await v2Api.reminders.snooze(r.id, 7);
      toast.success("Snoozed 7d");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Snooze failed");
    }
  };
  const onCompleteReminder = async (r) => {
    try {
      // Mark as inactive — user has confirmed it's done.
      await v2Api.reminders.patch(r.id, { is_active: false });
      toast.success("Marked complete");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Update failed");
    }
  };

  const filtered = useMemo(() => {
    let list = products;
    if (filter === "active") {
      list = list.filter((p) => p.active_bids > 0);
    } else if (filter === "completed") {
      list = list.filter((p) => p.active_bids === 0 && (p.won_count + p.lost_count) > 0);
    }
    if (q) {
      const ql = q.toLowerCase();
      list = list.filter((p) =>
        (p.product_name || "").toLowerCase().includes(ql) ||
        (p.product_code || "").toLowerCase().includes(ql) ||
        (p.category || "").toLowerCase().includes(ql)
      );
    }
    return list;
  }, [products, filter, q]);

  const hellos = currentUser?.full_name?.split(" ")[0] || currentUser?.name?.split(" ")[0] || "";

  return (
    <div className="px-4 sm:px-6 py-6 max-w-6xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Activity className="w-5 h-5 text-blue-600" />
          {hellos ? `Hi ${hellos},` : "Feed"}
        </h1>
        <p className="text-sm text-slate-500">
          AI-aggregated pulse on every product in your portfolio — pipeline, win rate,
          live RFP signals, alerts, and reminders.
        </p>
      </div>

      <TodayStrip items={todayItems} navigate={navigate} />

      <StatsStrip stats={stats} />

      <RemindersStrip
        reminders={reminders}
        onSnooze={onSnoozeReminder}
        onComplete={onCompleteReminder}
      />

      <div className="flex flex-wrap items-center gap-2 sticky top-0 bg-white z-10 py-2 border-b">
        <div className="flex gap-1">
          {[
            { id: "all", label: "All products" },
            { id: "active", label: "Active pipeline" },
            { id: "completed", label: "Completed" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`text-xs px-3 py-1 rounded-full border ${
                filter === f.id
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex-1 min-w-[180px]">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by product / code / category…"
            className="h-8 text-sm"
          />
        </div>
        <button
          onClick={() => navigate("/v2/catalog")}
          className="text-xs text-slate-500 hover:text-blue-600 flex items-center gap-1"
        >
          <Settings className="w-3 h-3" /> Manage catalog
        </button>
      </div>

      {loading && filtered.length === 0 && (
        <p className="text-sm text-slate-500 py-10 text-center">Computing portfolio…</p>
      )}

      {!loading && filtered.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-slate-400 text-sm">
            <ListChecks className="w-10 h-10 mx-auto mb-2 opacity-40" />
            No products match this filter. Add products under{" "}
            <button onClick={() => navigate("/v2/catalog")} className="text-blue-600 hover:underline">
              Catalog &amp; Rates
            </button>{" "}
            to populate the feed.
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {filtered.map((p) => (
          <ProductCard key={p.product_id} p={p} navigate={navigate} />
        ))}
      </div>
    </div>
  );
}
